import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { wizardApi, WizardProgress, LocationStepData, MusicStepData, ScenarioStepData } from '../services/wizardApi';
import { useAuth } from '../features/auth/AuthProvider';

export type WizardStep = 'welcome' | 'auth' | 'location' | 'music' | 'scenario' | 'preview' | 'subscription';

const WIZARD_STEPS: WizardStep[] = ['welcome', 'auth', 'location', 'music', 'scenario', 'preview', 'subscription'];

export interface UseWizardOptions {
  onComplete?: () => void;
}

export interface UseWizardReturn {
  // State
  currentStep: WizardStep;
  progress: WizardProgress | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  
  // Navigation
  goToStep: (step: WizardStep) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  
  // Step completion
  completeAuthStep: (email: string, password: string, fullName: string) => Promise<void>;
  completeOAuthStep: (provider: 'google' | 'microsoft' | 'apple') => Promise<void>;
  completeLocationStep: (data: LocationStepData) => Promise<void>;
  completeMusicStep: (data: MusicStepData) => Promise<void>;
  completeScenarioStep: (data: ScenarioStepData) => Promise<void>;
  completePreviewStep: () => Promise<void>;
  completeSubscriptionStep: (plan: string) => Promise<void>;
  
  // Utilities
  getStepIndex: (step: WizardStep) => number;
  isStepComplete: (step: WizardStep) => boolean;
  resetError: () => void;
}

/**
 * useWizard - Main hook for managing wizard state and navigation
 * 
 * Handles:
 * - Step navigation with validation
 * - Progress persistence to backend
 * - Authentication state
 * - Resume from saved progress
 */
export function useWizard(options: UseWizardOptions = {}): UseWizardReturn {
  const navigate = useNavigate();
  const { user, authUser, signIn } = useAuth();
  const [currentStep, setCurrentStep] = useState<WizardStep>('welcome');
  const [progress, setProgress] = useState<WizardProgress | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Authenticated via either the email-OTP session (primary) or an OIDC session.
  const isAuthenticated = !!authUser || (!!user && !user.expired);

  // Load progress on mount when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const initWizard = async () => {
      setIsLoading(true);
      try {
        const savedProgress = await wizardApi.getProgress();
        setProgress(savedProgress);

        if (!savedProgress.completed && savedProgress.currentStep) {
          setCurrentStep(savedProgress.currentStep as WizardStep);
        } else if (savedProgress.completed) {
          navigate('/app');
        }
      } catch (err) {
        console.error('Failed to initialize wizard:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initWizard();
  }, [isAuthenticated, navigate]);

  const getStepIndex = useCallback((step: WizardStep): number => {
    return WIZARD_STEPS.indexOf(step);
  }, []);

  const isStepComplete = useCallback((step: WizardStep): boolean => {
    if (!progress) return false;
    const stepIndex = getStepIndex(step);
    const currentIndex = getStepIndex(progress.currentStep as WizardStep);
    return stepIndex < currentIndex || progress.completed;
  }, [progress, getStepIndex]);

  const goToStep = useCallback((step: WizardStep) => {
    // Only allow going to completed steps or current step
    const targetIndex = getStepIndex(step);
    const currentIndex = getStepIndex(currentStep);
    
    if (targetIndex <= currentIndex || isStepComplete(step)) {
      setCurrentStep(step);
      setError(null);
    }
  }, [currentStep, getStepIndex, isStepComplete]);

  const goToNext = useCallback(() => {
    const currentIndex = getStepIndex(currentStep);
    if (currentIndex < WIZARD_STEPS.length - 1) {
      setCurrentStep(WIZARD_STEPS[currentIndex + 1]);
      setError(null);
    }
  }, [currentStep, getStepIndex]);

  const goToPrevious = useCallback(() => {
    const currentIndex = getStepIndex(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(WIZARD_STEPS[currentIndex - 1]);
      setError(null);
    }
  }, [currentStep, getStepIndex]);

  const canGoNext = getStepIndex(currentStep) < WIZARD_STEPS.length - 1;
  const canGoPrevious = getStepIndex(currentStep) > 0;

  // Auth step — Zitadel hosts the actual signup/login UI (email/password, MFA,
  // and federated IdPs all live there). Both completeAuthStep and
  // completeOAuthStep collapse into a single signinRedirect; the form/buttons
  // on the auth wizard step now exist as visual affordances that funnel into
  // the same flow. After Zitadel issues a token the callback page bounces
  // back into the wizard (which resumes from the persisted step).
  const completeAuthStep = useCallback(async (_email: string, _password: string, _fullName: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await signIn('/onboarding/wizard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start sign-in');
      setIsLoading(false);
      throw err;
    }
  }, [signIn]);

  const completeOAuthStep = useCallback(async (_provider: 'google' | 'microsoft' | 'apple') => {
    setIsLoading(true);
    setError(null);
    try {
      await signIn('/onboarding/wizard');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'OAuth sign-in failed');
      setIsLoading(false);
      throw err;
    }
  }, [signIn]);

  // Location step
  const completeLocationStep = useCallback(async (data: LocationStepData) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedProgress = await wizardApi.completeLocationStep(data);
      setProgress(updatedProgress);
      goToNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save location');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [goToNext]);

  // Music step
  const completeMusicStep = useCallback(async (data: MusicStepData) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedProgress = await wizardApi.completeMusicStep(data);
      setProgress(updatedProgress);
      goToNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save music preferences');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [goToNext]);

  // Scenario step
  const completeScenarioStep = useCallback(async (data: ScenarioStepData) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedProgress = await wizardApi.completeScenarioStep(data);
      setProgress(updatedProgress);
      goToNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create scenario');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [goToNext]);

  // Preview step
  const completePreviewStep = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedProgress = await wizardApi.completePreviewStep();
      setProgress(updatedProgress);
      goToNext();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm preview');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [goToNext]);

  // Subscription step
  const completeSubscriptionStep = useCallback(async (plan: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await wizardApi.completeSubscriptionStep(plan);
      
      // All plans start with a free trial — wizard completes immediately
      setProgress(result);
      options.onComplete?.();
      navigate('/app');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process subscription');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [navigate, options]);

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  return {
    currentStep,
    progress,
    isLoading,
    error,
    isAuthenticated,
    goToStep,
    goToNext,
    goToPrevious,
    canGoNext,
    canGoPrevious,
    completeAuthStep,
    completeOAuthStep,
    completeLocationStep,
    completeMusicStep,
    completeScenarioStep,
    completePreviewStep,
    completeSubscriptionStep,
    getStepIndex,
    isStepComplete,
    resetError,
  };
}

export default useWizard;
